/**
 * Layout linter (SD-14, STAGE-DESIGN §4) — pure local static analysis over a
 * project. Feeds the pre-flight (SD-13 row 8) and can run standalone in edit
 * mode. Pure: takes the project + resolvers, returns findings. No store reads,
 * so it's reusable from pre-flight, a lint panel, or a test.
 *
 * v1 checks:
 *   1. `dup-cc` — two CC-sending controls claim the SAME (channel, cc) on the
 *      SAME page. The "why does my filter also mute the vocal" class. The
 *      headline check; enriched with the Matrix's label for that (channel, cc)
 *      when a `matrixLabel` resolver is supplied (SD-14: the Matrix is the CC
 *      source of truth).
 *   2. `out-of-bounds` — an item extends past the canvas edge (won't render
 *      fully / unreachable on-device).
 *   3. `dangling-timeline` — a Timeline Launcher points at a timeline that no
 *      longer exists in the library.
 *   4. `unassigned` - a control points at nothing at all (Stream Deck key with
 *      no action, Timeline Launcher with no timeline). Shares `isUnassigned()`
 *      with the soft-red pulse, and reports the same fact in the pre-flight that
 *      runs when performance mode turns on.
 *
 * Channel normalization: item channel bases differ (button/fader/xy/lfo/grid
 * store 0-based, waves stores 1-based). Everything is normalized to 0-based
 * here so cross-item comparison + the Matrix lookup use one convention.
 */

import { buttonEmitsMidi, isUnassigned, type LayoutItem, type Project } from '../layout/projectStore.ts';
import { canvasSize, canvasSizeOf } from '../layout/devices.ts';

export type LintSeverity = 'warn' | 'error';

export interface LintFinding {
  code: 'dup-cc' | 'out-of-bounds' | 'dangling-timeline' | 'unassigned';
  severity: LintSeverity;
  message: string;
  pageId: string;
  pageName: string;
  /** Item(s) the finding implicates. */
  itemIds: string[];
}

export interface LintOptions {
  /** Timeline ids that currently exist — enables the dangling-timeline check.
   *  Omit to skip it (e.g. when the timeline library isn't available). */
  timelineIds?: Set<string>;
  /** Matrix lookup: a human label for a (channel0, cc) claim, or null if the
   *  Matrix doesn't cover it. `channel0` is 0-based. Used to enrich dup-cc
   *  messages ("… = 'Filter Cutoff' per the Matrix"). Omit to skip enrichment. */
  matrixLabel?: (channel0: number, cc: number) => string | null;
}

interface CcClaim {
  /** 'cc' and 'pc' are separate namespaces on the same channel — CC 5 and
   *  PC 5 are unrelated messages and must never collide with each other. */
  space: 'cc' | 'pc';
  channel0: number; // 0-based
  cc: number;        // the CC number OR the PC program number, per `space`
  itemId: string;
  label: string;    // short, human — "Button", "Grid cell 3", "XY X"
}

/** Human item label for a finding, preferring the authored name. */
function itemLabel(item: LayoutItem): string {
  const named = (item as { name?: string }).name;
  if (named && named.trim()) return named.trim();
  return item.kind;
}

/** Extract every CC send-claim an item makes, normalized to 0-based channel.
 *  Note-sending items (noteon buttons, piano) and OSC items (transpose, capo,
 *  mixer) don't participate in the v1 CC-collision check.
 *
 *  KEEP IN STEP WITH `layout/midiAlloc.ts`'s `midiSlotsOf`. They answer two
 *  halves of one question - this one "who already claims this CC", that one
 *  "which CC is free" - so a kind added to one and not the other produces a
 *  duplicate the linter never reports, or an allocator that hands out a
 *  number something else is already on. `radial` was exactly that: it lands
 *  a CC like a fader and was missing here from the day the kind shipped
 *  (DEC-116), so radial-vs-fader collisions passed pre-flight clean. Fixed
 *  2026-08-09 when the allocator forced the two lists to be compared.
 *
 *  The two are NOT merged into one function on purpose: `midiSlotsOf` also
 *  covers the NOTE namespace and includes read-only claims this check must
 *  not flag. They ALSO deliberately diverge on `pc` (see the `button` case
 *  below): `midiAlloc.ts` skips it because "next free" is meaningless for a
 *  program number (patch 7 IS patch 7), but that is an ALLOCATION reason,
 *  not a COLLISION reason — two buttons both wired to PC 5 ch 1 is still a
 *  real authoring mistake worth flagging, so this function does not skip it.
 *  That is not a bug in the sync; the two files answer different questions. */
function ccClaims(item: LayoutItem): CcClaim[] {
  const name = itemLabel(item);
  switch (item.kind) {
    case 'button': {
      const actionType = item.actionType ?? 'cc';
      if (actionType === 'noteon') return []; // note namespace, not this check
      // Nothing that doesn't emit MIDI can collide in the CC namespace - an
      // Ableton Control button rides OSC, an OBS button rides an HTTP verb,
      // an Ableton Device button rides OSC too. Without this they fell
      // through to the `cc` claim at the bottom and claimed `item.ccNumber`,
      // which their spawn helpers set to a placeholder 0 precisely because
      // nothing reads it. Two Ableton Control buttons on the same channel
      // therefore linted as a duplicate cc-0 collision that does not exist,
      // and a third control genuinely on cc 0 would be flagged against a
      // phantom. Found 2026-09-19 while adding `obs`.
      //
      // Asked as `buttonEmitsMidi` rather than by naming the exempt types,
      // so the NEXT non-MIDI action type is exempt by construction instead of
      // by whoever adds it remembering this file exists - see that
      // predicate's own comment for why that default is the safe one.
      if (!buttonEmitsMidi(actionType)) return [];
      if (actionType === 'pc') {
        // Safe to lint (unlike presetChange/snapshotChange below): a plain
        // Button's `pc` action has no per-item port field at all — per its
        // own schema doc, setting one "would quietly route that button away
        // from Ableton, which is why it is not surfaced in the general
        // Button inspector." It always fires on the rig's default port, so
        // two `pc` buttons on the same (channel, program) can never be
        // legitimately targeting two different devices — there is no port
        // ambiguity for the linter to get wrong here.
        return [{ space: 'pc', channel0: item.channel, cc: item.program ?? 0, itemId: item.id, label: name }];
      }
      return [{ space: 'cc', channel0: item.channel, cc: item.ccNumber, itemId: item.id, label: name }];
    }
    case 'fader':
    case 'lfo':
    case 'radial':
      return [{ space: 'cc', channel0: item.channel, cc: item.ccNumber, itemId: item.id, label: name }];
    case 'xypad':
      return [
        { space: 'cc', channel0: item.xChannel, cc: item.xCcNumber, itemId: item.id, label: `${name} · X` },
        { space: 'cc', channel0: item.yChannel, cc: item.yCcNumber, itemId: item.id, label: `${name} · Y` },
      ];
    case 'buttongrid':
      return item.cells.map((cell, i) => ({
        space: 'cc' as const,
        channel0: cell.channel ?? item.channel,
        cc: cell.ccNumber ?? ((item.baseCC + i) & 0x7f),
        itemId: item.id,
        label: `${name} · cell ${i + 1}`,
      }));
    case 'wavesAutotune': {
      const ch0 = (item.channel ?? 2) - 1; // waves stores 1-based
      return [
        { space: 'cc', channel0: ch0, cc: 55, itemId: item.id, label: `${name} · key` },
        { space: 'cc', channel0: ch0, cc: 56, itemId: item.id, label: `${name} · scale` },
      ];
    }
    case 'presetChange':
    case 'snapshotChange':
      // Deliberately claims NOTHING, though it does send CC32 + PC + CC69.
      //
      // Unlike a plain `pc` button (above), these resolve their MIDI port
      // live from `pedalId` (DEC-152) — a device-bound control, not a rig-
      // default one. This linter has no concept of ports - it collides
      // purely on (channel, number). Claiming them would therefore raise
      // FALSE conflicts between two DIFFERENT pedals that legitimately
      // share a channel + preset/CC number on their own separate ports,
      // which is exactly the case DEC-152's own conflict check scopes to
      // (port, channel) together for. They are also device-fixed numbers,
      // not rig-allocatable ones.
      //
      // Do not "fix" this by adding claims unless the linter first learns
      // about ports.
      return [];
    default:
      return [];
  }
}

const BOUNDS_TOL = 1; // px slack so a pixel-rounding overhang isn't flagged

export function lintProject(project: Project, opts: LintOptions = {}): LintFinding[] {
  const findings: LintFinding[] = [];
  const canvas = canvasSizeOf(project);

  for (const page of project.pages) {
    // ── 1. Duplicate (space, channel, cc) among CC/PC-sending controls ──
    // `space` is part of the key so a CC 5 claim and a PC 5 claim on the
    // same channel never cross-collide - they are unrelated messages.
    const byKey = new Map<string, CcClaim[]>();
    for (const item of page.items) {
      for (const claim of ccClaims(item)) {
        const key = `${claim.space}:${claim.channel0}:${claim.cc}`;
        const arr = byKey.get(key);
        if (arr) arr.push(claim);
        else byKey.set(key, [claim]);
      }
    }
    for (const [key, claims] of byKey) {
      if (claims.length < 2) continue;
      const [space, channel0Str, ccStr] = key.split(':') as ['cc' | 'pc', string, string];
      const channel0 = Number(channel0Str);
      const cc = Number(ccStr);
      // Matrix enrichment only understands CC lookups (pre-flight wires
      // `matrix/store.ts::matrixCcLabel`, 2026-10-18); skip it for PC so a
      // PC number is never misresolved as a CC one.
      const mx = space === 'cc' ? opts.matrixLabel?.(channel0, cc) : null;
      const who = claims.map((c) => c.label).join(', ');
      const noun = space === 'pc' ? 'Program Change' : 'CC';
      findings.push({
        code: 'dup-cc',
        severity: 'warn',
        message: `${noun} ${cc} · ch ${channel0 + 1}${mx ? ` (${mx})` : ''} is sent by ${claims.length} controls: ${who}`,
        pageId: page.id,
        pageName: page.name,
        itemIds: [...new Set(claims.map((c) => c.itemId))],
      });
    }

    // ── 2. Items outside the canvas bounds ──
    for (const item of page.items) {
      const right = item.x + item.w;
      const bottom = item.y + item.h;
      const over =
        item.x < -BOUNDS_TOL || item.y < -BOUNDS_TOL ||
        right > canvas.w + BOUNDS_TOL || bottom > canvas.h + BOUNDS_TOL;
      if (over) {
        findings.push({
          code: 'out-of-bounds',
          severity: 'warn',
          message: `"${itemLabel(item)}" extends past the ${canvas.w}×${canvas.h} canvas edge`,
          pageId: page.id,
          pageName: page.name,
          itemIds: [item.id],
        });
      }
    }

    // ── 3. Timeline Launchers pointing at a missing timeline ──
    if (opts.timelineIds) {
      for (const item of page.items) {
        if (item.kind !== 'timelineLauncher') continue;
        const tid = item.timelineId;
        if (tid && !opts.timelineIds.has(tid)) {
          findings.push({
            code: 'dangling-timeline',
            severity: 'error',
            message: `"${itemLabel(item)}" launches a timeline that no longer exists`,
            pageId: page.id,
            pageName: page.name,
            itemIds: [item.id],
          });
        }
      }
    }

    // ── 4. Controls that point at NOTHING ──
    // Added 2026-07-30 alongside the soft red unassigned pulse, when the pulse
    // was suppressed in performance mode and this stood in for it there. The
    // suppression retired with DEC-216 (performance mode changes no view), so
    // the pulse now shows all the time and this reports the same fact once
    // more in the pre-flight that runs when the switch turns on.
    //
    // Note how narrow check 3 above turned out to be: it only fires on a
    // timelineId that USED to resolve. A launcher that never had one, and a
    // Stream Deck key that was never assigned, both passed pre-flight clean and
    // then did nothing when pressed - the exact "looks alive, is dead" failure
    // the Stream Deck component was rebuilt to prevent.
    //
    // `isUnassigned` is shared with the pulse, so the two surfaces cannot
    // disagree about what "assigned" means.
    for (const item of page.items) {
      if (!isUnassigned(item)) continue;
      findings.push({
        code: 'unassigned',
        // error, not warn: unlike an off-canvas item you cannot see this one
        // is broken by looking at it on stage - that is the point of it.
        severity: 'error',
        message: `"${itemLabel(item)}" is not pointed at anything yet - pressing it will do nothing`,
        pageId: page.id,
        pageName: page.name,
        itemIds: [item.id],
      });
    }
  }

  return findings;
}
